import { useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { EVENTS, type CHIEvent } from "@/lib/eventsData";

type PromotionMode = "off" | "button" | "hero";

/**
 * One shared edit surface for the per-event CRM editor and Website Content.
 * Settings and mutation state live here so both entry points use the same save path.
 */
export default function HomepagePromotionControl({
  eventSlug,
  readOnly = false,
}: {
  /** Omit to expose the global event selector. */
  eventSlug?: string;
  readOnly?: boolean;
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const utils = trpc.useUtils();
  const settingsQuery = trpc.homepagePromotion.settings.useQuery(undefined, { retry: false });
  const catalogQuery = trpc.events.publicCatalog.useQuery(undefined, { retry: false });
  const [draftSlug, setDraftSlug] = useState(eventSlug ?? "");
  const [draftMode, setDraftMode] = useState<PromotionMode>("off");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const dirtyRef = useRef(false);
  const settings = settingsQuery.data as (typeof settingsQuery.data & { eventAvailable?: boolean }) | undefined;

  const events = useMemo(() => {
    const managed = (catalogQuery.data ?? []) as CHIEvent[];
    const managedSlugs = new Set(managed.map(item => item.slug));
    return [
      ...EVENTS.filter(item => !managedSlugs.has(item.slug)),
      ...managed,
    ];
  }, [catalogQuery.data]);
  const selected = events.find(item => item.slug === (eventSlug ?? draftSlug));
  const currentSlug = settings?.eventSlug ?? null;
  const currentEvent = currentSlug ? events.find(item => item.slug === currentSlug) : undefined;
  const currentAvailable = Boolean(currentSlug && settings?.eventAvailable !== false && currentEvent);

  useEffect(() => {
    if (dirtyRef.current) return;
    const nextSlug = eventSlug ?? settings?.eventSlug ?? "";
    setDraftSlug(nextSlug);
    setDraftMode(settings?.eventSlug === nextSlug ? settings.mode : "off");
  }, [eventSlug, settings]);

  const save = trpc.homepagePromotion.set.useMutation({
    onSuccess: async () => {
      dirtyRef.current = false;
      setError(null);
      setSaved(true);
      await utils.homepagePromotion.settings.invalidate();
    },
    onError: mutationError => {
      setSaved(false);
      setError(mutationError.message || "Homepage promotion could not be saved.");
    },
  });

  const targetSlug = eventSlug ?? draftSlug;
  const targetEvent = events.find(item => item.slug === targetSlug);
  const targetAvailable = Boolean(targetSlug && targetEvent && (targetSlug !== currentSlug || settings?.eventAvailable !== false));
  const slugOptions = useMemo(() => {
    const result = [...events];
    const selectedSlug = currentSlug;
    if (selectedSlug && !result.some(item => item.slug === selectedSlug)) {
      result.push({
        id: -1,
        slug: selectedSlug,
        title: selectedSlug,
      } as CHIEvent);
    }
    return result.sort((a, b) => a.title.localeCompare(b.title));
  }, [currentSlug, events]);

  function selectSlug(value: string) {
    dirtyRef.current = true;
    setDraftSlug(value);
    setDraftMode(settings?.eventSlug === value ? settings.mode : "off");
    setError(null);
    setSaved(false);
  }

  function chooseMode(mode: PromotionMode) {
    dirtyRef.current = true;
    setDraftMode(mode);
    setError(null);
    setSaved(false);
  }

  const disabled = !isAdmin || readOnly || settingsQuery.isLoading || catalogQuery.isLoading || save.isPending;

  return (
    <section aria-labelledby="homepage-promotion-control-title" style={{ padding: ".9rem", border: "1px solid rgba(244,114,182,.2)", borderRadius: ".65rem", background: "rgba(233,30,140,.045)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
        <span id="homepage-promotion-control-title" style={{ color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".68rem", fontWeight: 750, letterSpacing: ".08em", textTransform: "uppercase" }}>Homepage promotion</span>
        {currentSlug && (
          <span style={{ color: currentAvailable ? "#CBD5E1" : "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".68rem" }}>
            Currently featured: {currentEvent?.title ?? currentSlug}{currentAvailable ? "" : " · unavailable"}
          </span>
        )}
      </div>
      <p style={{ margin: ".35rem 0 .65rem", color: "#AEB9C9", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".74rem", lineHeight: 1.5 }}>
        One event can be featured at a time. Your choice stays a draft until saved. Prices and ticket actions come from the selected event’s live policy and quote.
      </p>
      {!eventSlug && (
        <label style={{ display: "block", marginBottom: ".7rem" }}>
          <span style={{ display: "block", marginBottom: ".3rem", color: "#AEB9C9", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".68rem", fontWeight: 650 }}>Featured event</span>
          <select aria-label="Featured event" value={draftSlug} disabled={disabled} onChange={event => selectSlug(event.target.value)} style={{ width: "100%", minHeight: 42, padding: ".45rem .6rem", border: "1px solid rgba(255,255,255,.16)", borderRadius: ".45rem", background: "#111729", color: "#F8FAFC", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".82rem" }}>
            <option value="">Choose an event</option>
            {slugOptions.map(item => (
              <option key={item.slug} value={item.slug}>
                {item.title}{item.slug === currentSlug && !currentAvailable ? " — unavailable" : ""}
              </option>
            ))}
          </select>
        </label>
      )}
      {eventSlug && <p style={{ margin: "0 0 .65rem", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".75rem" }}>This event: {targetEvent?.title ?? eventSlug}{targetAvailable ? "" : " · unavailable"}</p>}
      <div role="group" aria-label="Homepage promotion display" style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: ".4rem" }}>
        {([
          ["off", "Off"],
          ["button", "Button"],
          ["hero", "Hero"],
        ] as const).map(([mode, label]) => (
          <button key={mode} type="button" disabled={disabled} aria-pressed={draftMode === mode} onClick={() => chooseMode(mode)} style={{ minHeight: 40, border: `1px solid ${draftMode === mode ? "rgba(244,114,182,.72)" : "rgba(255,255,255,.12)"}`, borderRadius: ".4rem", background: draftMode === mode ? "rgba(233,30,140,.16)" : "rgba(255,255,255,.035)", color: draftMode === mode ? "#F9A8D4" : "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".74rem", fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer" }}>{label}</button>
        ))}
      </div>
      {settingsQuery.isError && <p role="alert" style={{ margin: ".55rem 0 0", color: "#FDA4AF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".72rem" }}>Promotion settings could not be loaded. Retry by reopening this view.</p>}
      {catalogQuery.isError && <p role="alert" style={{ margin: ".55rem 0 0", color: "#FDA4AF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".72rem" }}>The event list could not be loaded.</p>}
      {error && <p role="alert" style={{ margin: ".55rem 0 0", color: "#FDA4AF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".72rem" }}>{error}</p>}
      {saved && <p role="status" style={{ margin: ".55rem 0 0", color: "#86EFAC", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".72rem" }}>Homepage promotion saved.</p>}
      {!isAdmin && <p style={{ margin: ".5rem 0 0", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".7rem" }}>Only administrators can change the homepage promotion.</p>}
      {isAdmin && !readOnly && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: ".65rem" }}>
          <button type="button" disabled={disabled || !targetSlug || settingsQuery.isError || catalogQuery.isError} onClick={() => { setError(null); setSaved(false); save.mutate({ eventSlug: targetSlug, mode: draftMode }); }} style={{ minHeight: 38, padding: ".45rem .8rem", border: "1px solid rgba(244,114,182,.35)", borderRadius: ".45rem", background: "rgba(233,30,140,.12)", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".73rem", fontWeight: 700, cursor: disabled ? "wait" : "pointer" }}>{save.isPending ? "Saving…" : "Save homepage promotion"}</button>
        </div>
      )}
    </section>
  );
}
